import { SyntheticEvent, useState, useMemo, useContext } from "react";
import {
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  ListItem,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import Panel from "@/features/Common/components/Panel";
import theme, { defaultFullscreenPageStyling } from "@/theme";
import { useNavigate } from "react-router-dom";
import { trackEvent, trackPageViewV2 } from "@/services/analytics-adapter";
import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
  mapErrorToMessage,
} from "@/utils";
import {
  ApplyVoucherResponse,
  VoucherOrderStatus,
} from "@/services/core-api-adapter";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import DefaultError from "@/features/Common/components/DefaultError";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";
import IconLoader from "@/features/Common/components/IconLoader";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import DiscountVoucherCaptureModal, {
  CurrentPath,
} from "../DiscountVoucherCaptureModal";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import BottomFixedCallToAction from "../BottomFixedCallToAction";
import { SnackbarContext } from "@/context/SnackbarProvider";

export enum VoucherDisplayType {
  DiscountVoucher = "DiscountVoucher",
  CareCardVoucher = "CareCardVoucher",
}

export interface VoucherData {
  voucherOrderId: string;
  name: string;
  expiryDate: string;
  assignedOn: string;
  usedOn?: string;
  voucherStatus: string;
  voucherCode: string;
  discountAmount?: number;
  currencyCode?: string;
  serviceOfferingType?: string;
}

interface VoucherPanelProps {
  voucher: VoucherData;
  onPanelClick?: (voucher: VoucherData) => void;
  onActionClick?: (voucher: VoucherData) => void;
  voucherDisplayType: VoucherDisplayType;
}

interface VoucherManagementProps {
  voucherDisplayType: VoucherDisplayType;
  title: string;
  memberVoucherItems: { vouchers: VoucherData[] } | null;
  isMemberVouchersLoading: boolean;
  memberVouchersFetchError: any;
  onPanelClick?: (voucher: VoucherData) => void;
  onActionClick?: (voucher: VoucherData) => void;
  backNavigationPath?: string;
  applyDiscountVoucher?: (
    voucherCode: string
  ) => Promise<ApplyVoucherResponse | null>;
  isLoading?: boolean;
  error?: any;
  isError?: boolean;
  resetError?: () => void;
}

function VoucherPanel({
  voucher,
  onPanelClick,
  onActionClick,
  voucherDisplayType,
}: VoucherPanelProps) {
  const { t } = useTranslation();

  const handlePanelClick = () => {
    if (onPanelClick) {
      onPanelClick(voucher);
    }
  };

  const handleActionClick = () => {
    if (onActionClick) {
      onActionClick(voucher);
    }
  };

  return (
    <Stack
      sx={{
        background: theme.palette.background.paper,
        cursor: onPanelClick ? "pointer" : "default",
        border: "none",
        width: "100%",
      }}
    >
      <Panel sx={{ width: "100%" }}>
        <Stack spacing={2}>
          <Stack
            aria-label={`voucherOrderId-${voucher.voucherOrderId}`}
            component="button"
            onClick={handlePanelClick}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            textAlign="left"
            sx={{
              border: "none",
              background: "transparent",
              cursor: onPanelClick ? "pointer" : "default",
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight="bold" color="neutral.500">
                {t("More.MyVouchers.Added.subtitle", {
                  date: formatDateToReadableString(
                    new Date(voucher.assignedOn)
                  ),
                })}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {voucherDisplayType === VoucherDisplayType.DiscountVoucher
                  ? t("More.myDiscountVouchers.voucher.title")
                  : t("More.MyVouchers.Added.card.title")}
              </Typography>
              <Typography variant="body2">{voucher.voucherCode}</Typography>
              <Typography variant="body1" fontWeight="bold">
                {t("More.MyVouchers.Added.card.Services.title")}
              </Typography>
              <Typography variant="body2">
                {voucher.name ||
                  t("More.myDiscountVouchers.voucher.allServices.title")}
              </Typography>
            </Stack>
            {onPanelClick && <IconLoader icon="ChevronNextIcon" />}
          </Stack>
          <Stack spacing={0.5} textAlign="left">
            <Typography variant="body2">
              {voucher.voucherStatus === VoucherOrderStatus.ASSIGNED &&
                t("More.MyVouchers.Added.card.footerText", {
                  date: formatDateToReadableString(
                    new Date(voucher.expiryDate)
                  ),
                })}
              {voucher.voucherStatus === VoucherOrderStatus.EXPIRED &&
                t("More.MyVouchers.Expired.card.footerText", {
                  date: formatDateToReadableString(
                    new Date(voucher.expiryDate)
                  ),
                })}
              {voucher.voucherStatus === VoucherOrderStatus.REDEEMED &&
                t("More.MyVouchers.Used.card.footerText", {
                  date: formatDateToReadableString(
                    new Date(voucher.usedOn || "")
                  ),
                })}
            </Typography>
          </Stack>

          {voucherDisplayType === VoucherDisplayType.DiscountVoucher &&
            voucher.discountAmount &&
            voucher.currencyCode && (
              <>
                <Divider />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body1" fontWeight="bold">
                    {t("More.myDiscountVouchers.total.title")}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatAmount(voucher.discountAmount, voucher.currencyCode)}
                  </Typography>
                </Stack>
              </>
            )}

          {voucherDisplayType === VoucherDisplayType.CareCardVoucher &&
            voucher.voucherStatus === VoucherOrderStatus.ASSIGNED && (
              <>
                <Stack spacing={1} textAlign="left">
                  <Divider />
                  <ButtonWithAnalytics
                    page="MyVouchers"
                    text={t("More.MyVouchers.Added.card.buttonText")}
                    intent="navigational"
                    variant="text"
                    onClick={handleActionClick}
                    size="small"
                    sx={{ justifyContent: "left", padding: 0 }}
                    endIcon={<IconLoader icon="ChevronNextIcon" />}
                  >
                    {t("More.MyVouchers.Added.card.buttonText")}
                  </ButtonWithAnalytics>
                </Stack>
              </>
            )}
        </Stack>
      </Panel>
    </Stack>
  );
}

function VoucherList({
  vouchers,
  onPanelClick,
  onActionClick,
  voucherDisplayType,
}: {
  vouchers: VoucherData[];
  onPanelClick?: (voucher: VoucherData) => void;
  onActionClick?: (voucher: VoucherData) => void;
  voucherDisplayType: VoucherDisplayType;
}) {
  return (
    <>
      {vouchers.map((voucher) => (
        <ListItem
          key={voucher.voucherOrderId}
          role="listitem"
          sx={{ padding: 0 }}
        >
          <VoucherPanel
            voucher={voucher}
            onPanelClick={onPanelClick}
            onActionClick={onActionClick}
            voucherDisplayType={voucherDisplayType}
          />
        </ListItem>
      ))}
    </>
  );
}

export default function MemberVouchers({
  title,
  memberVoucherItems: memberVouchers,
  isMemberVouchersLoading,
  memberVouchersFetchError,
  onPanelClick,
  onActionClick,
  backNavigationPath = "/home/more",
  voucherDisplayType,
  applyDiscountVoucher,
  isLoading,
  error,
  isError,
  resetError,
}: VoucherManagementProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const myVoucherRoutes = ["added", "used", "expired"];
  const [selectedTab, setSelectedTab] = useState<string>(myVoucherRoutes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVoucherCode, setCurrentVoucherCode] = useState<string | null>(
    null
  );

  const { showSnackbar } = useContext(SnackbarContext);

  function filterVouchersByStatus(voucherStatus: VoucherOrderStatus) {
    return (
      memberVouchers?.vouchers?.filter(
        (x) => x.voucherStatus === voucherStatus
      ) ?? []
    );
  }

  const addedVouchers = useMemo(
    () => filterVouchersByStatus(VoucherOrderStatus.ASSIGNED),
    [memberVouchers]
  );
  const usedVouchers = useMemo(
    () => filterVouchersByStatus(VoucherOrderStatus.REDEEMED),
    [memberVouchers]
  );
  const expiredVouchers = useMemo(
    () => filterVouchersByStatus(VoucherOrderStatus.EXPIRED),
    [memberVouchers]
  );

  let vouchersToShow: VoucherData[] = [];
  if (selectedTab === "added") {
    vouchersToShow = addedVouchers;
  } else if (selectedTab === "used") {
    vouchersToShow = usedVouchers;
  } else if (selectedTab === "expired") {
    vouchersToShow = expiredVouchers;
  }

  const errorMessage = useMemo(() => {
    return mapErrorToMessage(error, t);
  }, [error?.message]);

  const noVouchers = !vouchersToShow || vouchersToShow.length === 0;

  function onTabChange(_event: SyntheticEvent, newTab: string) {
    const transformedTabName = newTab
      .split("-")
      .map((word, index) => {
        if (index === 0) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

    trackEvent({
      event: `action.myVouchersTabClicked`,
      "myVouchersTabClicked.name": transformedTabName,
      source: title,
    });

    trackPageViewV2({
      pageName: `${title}:${transformedTabName}`,
      pageSubSection1: title,
      pageSubSection2: `${title}:${transformedTabName}`,
      pageCategory: title,
    });

    setSelectedTab(newTab);
  }

  if (isMemberVouchersLoading) {
    return <FullscreenLoadingIndicator />;
  }

  if (memberVouchersFetchError) {
    return (
      <DefaultError>
        <Typography variant="h2">{t("GenericError.title")}</Typography>
        <Typography pb={2}>{t("GenericError.content")}</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            onClick={() => navigate("/home")}
            variant="contained"
          >
            {t("common.comeBackButton")}
          </Button>
        </Stack>
      </DefaultError>
    );
  }

  return (
    <>
      <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
        <CircledBackButton
          showLabel={true}
          onClick={() => navigate(backNavigationPath)}
        />
        <Stack my={3}>
          <Typography variant="h2">{title}</Typography>
        </Stack>
        <Tabs
          value={selectedTab}
          onChange={onTabChange}
          variant="scrollable"
          scrollButtons={false}
          aria-label={t("MyHealth.tabNavigation.label")}
          textColor="primary"
          sx={{
            mx: defaultFullscreenPageStyling.px * -1,
            "& .MuiTabs-flexContainer::after": {
              content: '" "',
              minWidth: (theme) =>
                theme.spacing(defaultFullscreenPageStyling.px),
              position: "relative",
            },
          }}
        >
          <Tab
            value={myVoucherRoutes[0]}
            label={"Added" + " (" + addedVouchers?.length + ")"}
            sx={{
              ml: defaultFullscreenPageStyling.px,
            }}
          />
          <Tab
            value={myVoucherRoutes[1]}
            label={"Used" + " (" + usedVouchers?.length + ")"}
          />
          <Tab
            value={myVoucherRoutes[2]}
            label={"Expired" + " (" + expiredVouchers?.length + ")"}
          />
        </Tabs>
        <Stack
          component="ul"
          role="list"
          aria-label={title}
          spacing={2}
          mb={`${convertPxToRem(24)} !important`}
        >
          {noVouchers ? (
            <Stack
              spacing={2}
              alignItems="center"
              justifyContent="center"
              height="80vh"
              width="100%"
            >
              <WatermarkIcon
                height={130}
                width={130}
                color={theme.palette.accent2[100]}
                foreground={theme.palette.info.main}
                Icon={"TrolleyWithPlusIcon"}
              />
              <Typography
                variant="body1"
                fontWeight={600}
                color="neutral.light"
              >
                {t("More.MyVouchers.noVouchers.text", {
                  selectedTab,
                })}
              </Typography>
            </Stack>
          ) : (
            <VoucherList
              vouchers={vouchersToShow}
              onPanelClick={onPanelClick}
              onActionClick={onActionClick}
              voucherDisplayType={voucherDisplayType}
            />
          )}
        </Stack>
        {voucherDisplayType === VoucherDisplayType.DiscountVoucher &&
          isFeatureEnabled(
            FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
          ) && (
            <BottomFixedCallToAction
              buttonText="More.myDiscountVouchers.CTA.text"
              onButtonClick={() => {
                resetError?.();
                setIsModalOpen(true);
                setCurrentVoucherCode(null);
              }}
              ariaLabel="More.myDiscountVouchers.CTA.text"
              analyticsPageName={t("More.myDiscountVouchers.title")}
              buttonColor="primary"
              buttonVariant="outlined"
            />
          )}
      </Stack>

      {isLoading && <FullscreenLoadingIndicator mainIconType="face" />}

      {isModalOpen &&
        !isLoading &&
        voucherDisplayType === VoucherDisplayType.DiscountVoucher &&
        isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS) && (
          <DiscountVoucherCaptureModal
            isModalOpen={isModalOpen}
            onModalClose={() => {
              setIsModalOpen(false);
              resetError?.();
            }}
            errorMessage={errorMessage}
            currentPath={CurrentPath.MyDiscountVouchers}
            currentVoucherCode={currentVoucherCode}
            handleApplyVoucher={async (voucherCode: string) => {
              setCurrentVoucherCode(voucherCode);
              try {
                const response = await applyDiscountVoucher?.(voucherCode);
                const amount = formatAmount(
                  response?.memberOfferingUsage?.discountVoucherDetails
                    ?.discountAmount || 0,
                  response?.memberOfferingUsage?.discountVoucherDetails
                    ?.currencyCode || "ZAR"
                );
                if (response?.memberOfferingUsage) {
                  setIsModalOpen(false);
                  resetError?.();
                  setSelectedTab("added");
                  showSnackbar({
                    message: t(
                      "More.myDiscountVouchers.toast.success.message",
                      {
                        amount,
                      }
                    ),
                    type: "success",
                    duration: 10000,
                    position: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                    animate: true,
                    direction: "up",
                    offset: 100,
                    icon: (
                      <IconLoader
                        icon="CheckVerifiedIcon"
                        color="success"
                        sx={{ fontSize: convertPxToRem(20) }}
                      />
                    ),
                    borderLeftColor: theme.palette.success.main,
                  });
                }
              } catch (error) {
                console.error(new Error("Something went wrong"));
              }
            }}
          />
        )}
    </>
  );
}
